import { Trans } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import {
  isolationPointId,
  type LotoOtherPage,
} from 'features/lotoReports/lotoIsolationPoints'
import type {
  LotoAttachment,
  LotoIsolationPoint,
} from 'features/lotoReports/lotoReportSchemas'
import { type ReactElement, useCallback, useId, useState } from 'react'

import {
  LotoIsolationPointRow,
  type LotoPointLocation,
  type LotoPointRowActions,
} from './LotoIsolationPointRow'
import {
  LotoModeSwatch,
  LotoPointList,
  LotoPointsHeader,
  LotoSectionTitle,
} from './lotoPagePanel.styles'
import { LotoSortablePoints } from './LotoSortablePoints'

type LotoIsolationPointsTableProps = {
  points: LotoIsolationPoint[]
  currentPage: LotoAttachment
  otherPages: LotoOtherPage[]
  pagesLoading: boolean
  actions: LotoPointRowActions
}

function pointLocation(
  point: LotoIsolationPoint,
  currentPage: LotoAttachment,
  otherPages: LotoOtherPage[],
  loading: boolean
): LotoPointLocation {
  if (currentPage.page_ids.includes(point.page_id)) return { kind: 'thisPage' }
  return {
    kind: 'otherPage',
    page: otherPages.find(({ pageId }) => pageId === point.page_id),
    loading,
  }
}

function useExpandedPoints(): {
  expanded: ReadonlySet<string>
  toggle: (id: string) => void
  setExpanded: (ids: string[]) => void
} {
  const [expanded, setExpandedSet] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const toggle = useCallback(
    (id: string) =>
      setExpandedSet((current) => {
        const next = new Set(current)
        if (!next.delete(id)) next.add(id)
        return next
      }),
    []
  )
  const setExpanded = useCallback(
    (ids: string[]) => setExpandedSet(new Set(ids)),
    []
  )
  return { expanded, toggle, setExpanded }
}

function ExpandAllToggle({
  allExpanded,
  onToggle,
}: {
  allExpanded: boolean
  onToggle: () => void
}): ReactElement {
  return (
    <Button variant='ghost' size='xs' onClick={onToggle}>
      {allExpanded ? <Trans>Collapse all</Trans> : <Trans>Expand all</Trans>}
    </Button>
  )
}

export function LotoIsolationPointsTable({
  points,
  currentPage,
  otherPages,
  pagesLoading,
  actions,
}: LotoIsolationPointsTableProps): ReactElement {
  const headingId = useId()
  const { expanded, toggle, setExpanded } = useExpandedPoints()
  const rows = points.map((point) => ({
    point,
    id: isolationPointId(point),
    location: pointLocation(point, currentPage, otherPages, pagesLoading),
  }))
  const otherPageIds = rows
    .filter(({ location }) => location.kind === 'otherPage')
    .map(({ id }) => id)
  const allExpanded = otherPageIds.every((id) => expanded.has(id))
  return (
    <Box display='flex' direction='column' gap='xs'>
      <LotoPointsHeader>
        <LotoSectionTitle>
          <LotoModeSwatch $mode='points' />
          <Text as='h3' id={headingId} variant='caption' color='muted' truncate>
            <Trans>Isolation points</Trans>
          </Text>
        </LotoSectionTitle>
        {otherPageIds.length > 0 && (
          <ExpandAllToggle
            allExpanded={allExpanded}
            onToggle={() => setExpanded(allExpanded ? [] : otherPageIds)}
          />
        )}
      </LotoPointsHeader>
      {points.length === 0 ? (
        <Text variant='caption' color='muted'>
          <Trans>Click a node on the drawing to add an isolation point.</Trans>
        </Text>
      ) : (
        <LotoSortablePoints
          points={points}
          activator='handle'
          layout='list'
          onMove={actions.onMove}>
          <LotoPointList
            aria-labelledby={headingId}
            $togglable={otherPageIds.length > 0}>
            {rows.map(({ point, id, location }, index) => {
              const onThisPage = location.kind === 'thisPage'
              return (
                <LotoIsolationPointRow
                  key={id}
                  point={point}
                  index={index}
                  location={location}
                  expanded={onThisPage || expanded.has(id)}
                  onToggleExpanded={onThisPage ? undefined : () => toggle(id)}
                  actions={actions}
                />
              )
            })}
          </LotoPointList>
        </LotoSortablePoints>
      )}
    </Box>
  )
}
